
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { BillingPackageList } from '@/collections/billing_packages';
import { BillingPackageModel } from '@/models/billing_package';
import { StoreList } from '@/collections/stores';
import { StoreModel } from '@/models/store';

interface PackageConfig {
  billingPackageId: number | null;
  interval: string;
  trialEnabled: boolean;
  trialDays: number;
}

interface MerchantInfo {
  merchantId: number;
  planName: string;
}

@Component({
  components: {},
})
export default class PackageConfigBuilder extends Vue {
  @Prop({ default: () => ({}) })
  public value!: any;

  @Prop({ default: null })
  public targetedMerchantId!: number | null;

  public config: PackageConfig = {
    billingPackageId: null,
    interval: 'EVERY_30_DAYS',
    trialEnabled: false,
    trialDays: 7,
  };

  public errorMessage: string = '';
  public packagesLoading: boolean = false;
  public storesLoading: boolean = false;
  public overrideEnabled: boolean = false;
  public billingPackages: BillingPackageList = new BillingPackageList();
  public stores: StoreList = new StoreList();
  public targetedMerchantStore: StoreModel | null = null;
  public merchantLoadTimeout: number | null = null;

  get packageOptions() {
    if (!this.targetedMerchantId || !this.targetedMerchantStore) {
      return this.billingPackages.items.map((pkg: BillingPackageModel) => ({
        id: pkg.id,
        name: `${pkg.name} - $${pkg.price}`,
      }));
    }

    // Map the store's plan to the correct Shopify plan name (same logic as Warbler)
    const assistyPlan = this.getAssistyPlan(this.targetedMerchantStore.planName);
    
    // Debug logging
    console.log('Debug packageOptions:', {
      merchantPlanName: this.targetedMerchantStore.planName,
      assistyPlan: assistyPlan,
      allPackages: this.billingPackages.items.map(pkg => ({
        id: pkg.id,
        name: pkg.name,
        shopifyPlanName: pkg.shopifyPlanName,
        price: pkg.price
      }))
    });
    
    if (!assistyPlan) {
      return [];
    }

    // Filter packages based on assistyPlan matching shopifyPlanName
    const matchingPackages = this.billingPackages.items
      .filter((pkg: BillingPackageModel) => pkg.shopifyPlanName === assistyPlan);

    console.log('Debug matching packages:', matchingPackages.length, matchingPackages.map(pkg => pkg.name));
    console.log('Current config.billingPackageId:', this.config.billingPackageId);

    // If no packages found with matching shopifyPlanName, include all paid packages as fallback
    let packagesToShow = matchingPackages.length > 0 
      ? matchingPackages 
      : this.billingPackages.items.filter((pkg: BillingPackageModel) => pkg.price > 0);

    // Always include the currently selected package even if it doesn't match the plan filter
    if (this.config.billingPackageId) {
      const selectedPackage = this.billingPackages.items.find((pkg: BillingPackageModel) => pkg.id === this.config.billingPackageId);
      if (selectedPackage && !packagesToShow.find(pkg => pkg.id === selectedPackage.id)) {
        packagesToShow = [...packagesToShow, selectedPackage];
        console.log('Added currently selected package to options:', selectedPackage.name);
      }
    }

    const result = packagesToShow.map((pkg: BillingPackageModel) => ({
      id: pkg.id,
      name: `${pkg.name} - $${pkg.price}`,
    }));
    
    console.log('Final packageOptions result:', result.map(opt => ({ id: opt.id, name: opt.name })));
    return result;
  }

  get hasMatchingPackages(): boolean {
    if (!this.targetedMerchantStore?.planName) {
      return false;
    }

    const assistyPlan = this.getAssistyPlan(this.targetedMerchantStore.planName);
    if (!assistyPlan) {
      return false;
    }

    const matchingPackages = this.billingPackages.items
      .filter((pkg: BillingPackageModel) => pkg.shopifyPlanName === assistyPlan);

    return matchingPackages.length > 0;
  }

  get canEnableOverride(): boolean {
    // Can only enable override if:
    // 1. A merchant ID is targeted, AND
    // 2. The merchant store was successfully loaded
    return !!(this.targetedMerchantId && this.targetedMerchantStore);
  }

  get jsonPreview(): string {
    try {
      return JSON.stringify(this.config, null, 2);
    } catch (error) {
      return '{ "error": "Invalid configuration" }';
    }
  }

  public async created() {
    await this.loadBillingPackages();
    this.initializeFromValue();
    if (this.targetedMerchantId) {
      await this.loadTargetedMerchant();
    }
    // Emit initial state
    this.emitConfig();
  }

  public getAssistyPlan(planDisplayName: string | undefined): string {
    if (!planDisplayName) {
      return 'Shopify';
    }

    // Map Shopify plan names to the values stored in pkg.shopifyPlanName
    // This handles both plan_display_name from Shopify API and billing package names
    const planMappings: { [key: string]: string } = {
      // Direct Shopify plan names (from information.plan_display_name)
      'Basic': 'Basic',
      'Shopify': 'Shopify', 
      'Advanced': 'Advanced',
      'Plus': 'Plus',
      // Billing package names (fallback mapping)
      'Shopify Plus': 'Plus',
      'Advanced Shopify': 'Advanced', 
      'Basic Shopify': 'Basic',
      'Professional': 'Shopify',
      'Replenishment Center': 'Shopify',
      'Free': 'Basic',
    };

    // Direct lookup first
    if (planMappings[planDisplayName]) {
      return planMappings[planDisplayName];
    }

    // Case-insensitive partial matching
    const planLower = planDisplayName.toLowerCase();
    for (const [planName, shopifyPlan] of Object.entries(planMappings)) {
      if (planLower.includes(planName.toLowerCase())) {
        return shopifyPlan;
      }
    }
    
    return 'Shopify'; // Default fallback
  }

  public getMappedPlanDisplay(): string {
    if (!this.targetedMerchantStore?.planName) {
      return 'Unknown';
    }

    const mappedPlan = this.getAssistyPlan(this.targetedMerchantStore.planName);
    return `${mappedPlan} (${this.targetedMerchantStore.planName})`;
  }

  public async loadBillingPackages() {
    try {
      this.packagesLoading = true;
      await this.billingPackages.fetch();
    } catch (error) {
      this.errorMessage = 'Failed to load billing packages';
    } finally {
      this.packagesLoading = false;
    }
  }

  public async loadTargetedMerchant() {
    if (!this.targetedMerchantId) {
      this.targetedMerchantStore = null;
      return;
    }

    try {
      this.storesLoading = true;
      const store = new StoreModel();
      store.id = this.targetedMerchantId;
      await store.fetch();
      
      // Debug logging to see what data we got
      console.log('Debug loadTargetedMerchant:', {
        storeId: this.targetedMerchantId,
        planName: store.planName,
        storeName: store.name,
        rawData: store
      });
      
      this.targetedMerchantStore = store;
      
      // Use $nextTick to ensure the UI updates after the reactive data changes
      await this.$nextTick();
      this.validateConfig();
    } catch (error) {
      console.error('Failed to load merchant:', error);
      this.errorMessage = 'Failed to load targeted merchant details';
      this.targetedMerchantStore = null;
    } finally {
      this.storesLoading = false;
    }
  }

  public initializeFromValue() {
    if (this.value && Object.keys(this.value).length > 0) {
      console.log('PackageConfigBuilder initializeFromValue:', {
        value: this.value,
        billingPackageId: this.value.billingPackageId,
        packageOptions: this.packageOptions.map(opt => ({ id: opt.id, name: opt.name }))
      });
      this.config = { ...this.config, ...this.value };
      this.overrideEnabled = true;
    }
  }

  public onOverrideToggle() {
    if (!this.overrideEnabled) {
      // Clear configuration when disabled
      this.config = {
        billingPackageId: null,
        interval: 'EVERY_30_DAYS',
        trialEnabled: false,
        trialDays: 7,
      };
      this.emitConfig();
    }
  }

  @Watch('canEnableOverride')
  public onCanEnableOverrideChange(canEnable: boolean) {
    // If override can no longer be enabled, disable it
    if (!canEnable && this.overrideEnabled) {
      this.overrideEnabled = false;
      this.onOverrideToggle();
    }
  }

  public async copyToClipboard() {
    try {
      await navigator.clipboard.writeText(this.jsonPreview);
      // Could show a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }

  public validateConfig() {
    this.errorMessage = '';

    if (this.config.trialEnabled && (!this.config.trialDays || this.config.trialDays <= 0)) {
      this.errorMessage = 'Trial days must be greater than 0 when trial is enabled';
      return false;
    }

    this.emitConfig();
    return true;
  }

  public emitConfig() {
    // Only emit config if override is enabled, otherwise emit null
    if (this.overrideEnabled) {
      this.$emit('input', this.config);
    } else {
      this.$emit('input', null);
    }
  }

  @Watch('value', { deep: true })
  public onValueChange(newValue: any) {
    if (newValue && Object.keys(newValue).length > 0 && JSON.stringify(newValue) !== JSON.stringify(this.config)) {
      this.config = { ...this.config, ...newValue };
      this.overrideEnabled = true;
    } else if (!newValue || Object.keys(newValue).length === 0) {
      this.overrideEnabled = false;
    }
  }

  @Watch('billingPackages.items')
  public onBillingPackagesLoaded() {
    console.log('billingPackages loaded:', {
      count: this.billingPackages.items.length,
      value: this.value,
      currentConfig: this.config
    });
    // Reinitialize from value when billing packages are loaded
    // This ensures the dropdown gets populated correctly
    if (this.value && Object.keys(this.value).length > 0) {
      this.initializeFromValue();
    }
  }

  @Watch('config', { deep: true })
  public onConfigChange() {
    this.validateConfig();
  }

  @Watch('targetedMerchantId')
  public async onTargetedMerchantChange(newMerchantId: number | null, oldMerchantId: number | null) {
    // Clear existing timeout
    if (this.merchantLoadTimeout) {
      clearTimeout(this.merchantLoadTimeout);
    }

    if (newMerchantId) {
      // Only reset package selection if this is a user-initiated change (not initial load)
      const isUserChange = oldMerchantId !== null && oldMerchantId !== newMerchantId;
      
      // Set longer delay (2 seconds) before loading merchant info
      this.merchantLoadTimeout = setTimeout(async () => {
        await this.loadTargetedMerchant();
        
        // Reset package selection only for user-initiated merchant changes
        if (isUserChange) {
          this.config.billingPackageId = null;
          await this.$nextTick();
          this.validateConfig();
        }
      }, 2000);
    } else {
      this.targetedMerchantStore = null;
      this.config.billingPackageId = null;
      // Keep monthly as default when clearing merchant
      this.config.interval = 'EVERY_30_DAYS';
      
      // Use $nextTick to ensure the UI updates after clearing merchant
      await this.$nextTick();
      this.validateConfig();
    }
  }
}
